import { useRef, useCallback, useEffect } from 'react';

/**
 * Polls /exploration/case/{caseId}/chat/events/{conversationId}?since=N for new events.
 * Requires token for authentication.
 * 
 * Returns { start, stop, isPolling }.
 */
const PAGE_LIMIT = 500;

export default function useChatEvents(caseId, conversationId, { getToken, onEvents, onStatusChange, intervalMs = 500 } = {}) {
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const pollingRef = useRef(false);
  const cancelledRef = useRef(false);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    pollingRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback((sinceSeq = 0, overrideConvId = null) => {
    const convId = overrideConvId || conversationId;
    if (!convId) return;  // Can't poll without a conversation ID
    stop();
    cancelledRef.current = false;
    pollingRef.current = true;
    seqRef.current = sinceSeq;
    const pollCountRef = { current: 0 };

    async function poll() {
      if (cancelledRef.current) return;
      pollCountRef.current++;
      try {
        const url = new URL(`/api/v1/exploration/case/${caseId}/chat/events/${convId}`, window.location.origin);
        url.searchParams.set('since', String(seqRef.current));
        url.searchParams.set('limit', String(PAGE_LIMIT));

        const headers = {};
        const token = getToken?.();
        if (token && token !== 'dev-mode') {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(url, { headers });
        if (cancelledRef.current) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.events?.length) {
          const maxSeq = Math.max(...data.events.map(e => e.sequence ?? 0));
          if (maxSeq > seqRef.current) seqRef.current = maxSeq;
          onEvents?.(data.events);
        }

        if (data.status && data.status !== 'streaming') {
          if ((data.events?.length ?? 0) >= PAGE_LIMIT) {
            // Full page: more events (possibly the done event) remain
            // beyond this batch. Drain them before honoring the terminal
            // status, or the caller finalizes from an incomplete stream.
          } else if (pollCountRef.current <= 6 && (!data.events || data.events.length === 0)) {
            // Grace period: if this is the first few polls and no events
            // arrived yet, the background task may not have started writing
            // events. Don't stop polling prematurely — give it a chance.
          } else {
            onStatusChange?.(data.status);
            pollingRef.current = false;
            return; // done — stop polling
          }
        }
      } catch (err) {
        // network blip or auth error — keep polling
        console.debug('Chat events poll error:', err.message);
      }

      if (!cancelledRef.current) {
        timerRef.current = setTimeout(poll, intervalMs);
      }
    }

    poll();
  }, [caseId, conversationId, intervalMs, getToken, onEvents, onStatusChange, stop]);

  // Cleanup on unmount
  useEffect(() => stop, [stop]);

  return { start, stop, getSeq: () => seqRef.current };
}
